import { forwardRef, InputHTMLAttributes, LegacyRef, useState, ChangeEvent } from 'react';
import { cn } from '@/utils/classNames.ts';
import { InputProps } from 'react-daisyui';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

type Option<T> = {
  value: T;
  label: string;
};

type SelectInputProps<T> = { onChange?: (value: T) => void; options: Option<T>[] } & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'color' | 'onChange' | 'value'
> &
  Pick<InputProps, 'size' | 'color' | 'bordered'> & {
    label?: string;
    error?: string;
    wrapperClassName?: string;
    labelClassName?: string;
  } & { value: T; searchable?: boolean; searchablePlaceholder?: string };

const SelectInput = forwardRef(
  <T,>(
    {
      label,
      error,
      wrapperClassName,
      labelClassName,
      size = 'md',
      color,
      bordered = true,
      value,
      onChange,
      options,
      className,
      searchable = false,
      searchablePlaceholder = 'Search...',
      ...args
    }: SelectInputProps<T>,
    ref: LegacyRef<HTMLInputElement>
  ) => {
    const [searchQuery, setSearchQuery] = useState('');

    const handleSelect = (value: T) => {
      if (onChange) {
        onChange(value);
      }

      setSearchQuery('');

      // @ts-expect-error - blur() is a valid method
      document?.activeElement?.blur();
    };

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    };

    const availableOptions = !searchable
      ? options
      : options.filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()));

    return (
      <div className={cn('form-control max-w-full w-full', wrapperClassName)}>
        {label ? (
          <label className="label">
            <span className="label-text">{label}</span>
          </label>
        ) : null}
        <label
          className={cn(
            'input flex items-center justify-between gap-2 dropdown cursor-pointer max-w-full group',
            labelClassName,
            {
              'input-ghost': color === 'ghost',
              'input-primary': color === 'primary',
              'input-secondary': color === 'secondary',
              'input-accent': color === 'accent',
              'input-info': color === 'info',
              'input-success': color === 'success',
              'input-warning': color === 'warning',
              'input-error': error || color === 'error',
              'input-xs': size === 'xs',
              'input-sm': size === 'sm',
              'input-md': size === 'md' || size,
              'input-lg': size === 'lg',
              'input-bordered': bordered,
            }
          )}
        >
          <input
            tabIndex={0}
            className={cn('text-base grow cursor-pointer overflow-auto max-w-[calc(100%-1rem)]', className)}
            ref={ref}
            readOnly
            value={options.find((option) => option.value === value)?.label ?? ''}
            {...args}
          />
          <ul
            tabIndex={0}
            className={cn(
              'dropdown-content menu bg-base-100 rounded-box z-10 shadow-sm w-full absolute left-0 top-[calc(100%+5px)] max-h-[200px] overflow-y-auto flex-nowrap hidden group-focus-within:flex',
              {
                'p-2': !searchable,
                'px-2 pt-0 pb-2': searchable,
              }
            )}
          >
            {searchable ? (
              <li className="sticky top-0 py-2 z-10 bg-base-100">
                <input
                  placeholder={searchablePlaceholder}
                  className="bg-transparent border input-bordered cursor-text"
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                />
              </li>
            ) : null}
            {availableOptions.map((option) => (
              <li key={`${option.label}_${args.name ?? ''}_${option.value}`}>
                <button
                  type="button"
                  className={cn(
                    'active:bg-primary active:text-primary-content hover:bg-primary hover:bg-opacity-20 focus:bg-primary focus:bg-opacity-20',
                    {
                      '!bg-primary !text-primary-content': option.value === value,
                    }
                  )}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
          <ChevronDownIcon className="w-4 shrink-0" />
        </label>
        {error ? (
          <label className="label">
            <span className="label-text-alt text-error">{error}</span>
          </label>
        ) : null}
      </div>
    );
  }
) as <T>(props: SelectInputProps<T> & { ref?: LegacyRef<HTMLInputElement> }) => JSX.Element;

export default SelectInput;
